import React, { useState, useContext } from "react";
import { Link, useLocation } from "react-router-dom";
import { Navbar, NavbarBrand, NavItem, NavLink, Container } from "reactstrap";
import {
  getBasePath,
  getStoreBrandName,
  getStoreLogoPath,
  getStoreUserDisplayName,
} from "../utils";
import { AppContext } from "../context";
import SizingGrid from "./components/SizingGrid";

const NavBar = ({
  fullWidth,
  centeredColumn = false,
  onCartClick,
  onAuthClick,
  onEditProfileClick,
  cartItemCount = 0,
  isLoggedIn = false,
  hasAnyOrder = false,
  hasAnyCreditNote = false,
}) => {
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [isSizingGridOpen, setIsSizingGridOpen] = useState(false);
  const location = useLocation();
  const basePath = getBasePath();
  const brandName = getStoreBrandName();
  const logoPath = getStoreLogoPath();
  const { currentUser } = useContext(AppContext);
  const isDealerByType =
    typeof currentUser?.type === "string" &&
    currentUser.type.toLowerCase().includes("dealer");
  const isDealerUser = currentUser?.dealer === true || isDealerByType;
  const isAdminUser = currentUser?.admin === true;
  const isBuyerUser = isLoggedIn && !isDealerUser && !isAdminUser;
  const canShowSizeChart = !isLoggedIn || (isBuyerUser && currentUser?.customer === false);

  const userLabel = getStoreUserDisplayName(currentUser);

  const normalizePath = (path) => {
    const normalized = String(path || "").replace(/\/+$/, "");
    return normalized === "" ? "/" : normalized;
  };

  const currentPath = normalizePath(location.pathname);

  const isNavActive = (targetPath, exact = false) => {
    const target = normalizePath(targetPath);
    if (exact) return currentPath === target;
    return currentPath === target || currentPath.startsWith(`${target}/`);
  };

  const getNavLinkStyle = (active) => ({
    fontSize: "0.85rem",
    color: active ? "var(--bs-primary)" : "#FFFFFF",
  });

  return (
    <Navbar
      expand="lg"
      dark
      style={{
        position: centeredColumn ? "sticky" : "fixed",
        top: 0,
        width: "100%",
        backgroundColor: "rgba(0, 0, 0, 0.9)",
        zIndex: 1000,
        height: 60,
      }}
    >
      <Container
        className={`d-flex justify-content-between align-items-center p-0 ${
          fullWidth && "w-100 mw-100 px-4"
        }`}
      >
        {/* Left side - Logo and primary links */}
        <div className="d-flex align-items-center">
          <div className="d-flex align-items-center">
            {isDealerUser ? (
              <NavbarBrand
                tag={Link}
                to={`${basePath}/dealer/dashboard`}
                className="d-flex flex-column align-items-start mb-0 me-1"
              >
                <img src={logoPath} alt={`${brandName} logo`} height="25" />
              </NavbarBrand>
            ) : (
              <NavbarBrand
                tag={Link}
                to={`${basePath}/`}
                className="d-flex flex-column align-items-start me-0"
              >
                <img src={logoPath} alt={`${brandName} logo`} height="25" />
              </NavbarBrand>
            )}
          </div>

          {/* Navigation links next to logo (left aligned) */}
          {!isLoggedIn && (
            <NavItem className="ms-3">
              {(() => {
                const aboutPath = `${basePath}/about`;
                const isAboutActive = isNavActive(aboutPath, true);
                return (
                  <NavLink
                    tag={Link}
                    to={aboutPath}
                    className="px-0 fw-bold"
                    style={getNavLinkStyle(isAboutActive)}
                  >
                    About
                  </NavLink>
                );
              })()}
            </NavItem>
          )}
          {isDealerUser ? (
            <>
              <NavItem className="ms-3">
                {(() => {
                  const dealerDashboardPath = `${basePath}/dealer/dashboard`;
                  const isDashActive = isNavActive(dealerDashboardPath);
                  return (
                    <NavLink
                      tag={Link}
                      to={dealerDashboardPath}
                      className="px-0 fw-bold"
                      style={getNavLinkStyle(isDashActive)}
                    >
                      Home
                    </NavLink>
                  );
                })()}
              </NavItem>
              <NavItem className="ms-3">
                {(() => {
                  const productsPath = `${basePath}/products`;
                  const isProductsActive = isNavActive(productsPath);
                  return (
                    <NavLink
                      tag={Link}
                      to={productsPath}
                      className="px-0 fw-bold"
                      style={getNavLinkStyle(isProductsActive)}
                    >
                      Products
                    </NavLink>
                  );
                })()}
              </NavItem>
            </>
          ) : isAdminUser ? (
            <>
              <NavItem className="ms-3">
                {(() => {
                  const dashboardPath = `${basePath}/admin/dashboard`;
                  const isDashboardActive = isNavActive(dashboardPath);
                  return (
                    <NavLink
                      tag={Link}
                      to={dashboardPath}
                      className="px-0 fw-bold"
                      style={getNavLinkStyle(isDashboardActive)}
                    >
                      Dashboard
                    </NavLink>
                  );
                })()}
              </NavItem>
              <NavItem className="ms-3">
                {(() => {
                  const productsPath = `${basePath}/products`;
                  const isProductsActive = isNavActive(productsPath);
                  return (
                    <NavLink
                      tag={Link}
                      to={productsPath}
                      className="px-0 fw-bold"
                      style={getNavLinkStyle(isProductsActive)}
                    >
                      Products
                    </NavLink>
                  );
                })()}
              </NavItem>
            </>
          ) : (
            <>
              <NavItem className="ms-3">
                {(() => {
                  const productsPath = `${basePath}/products`;
                  const isProductsActive = isNavActive(productsPath);
                  return (
                    <NavLink
                      tag={Link}
                      to={productsPath}
                      className="px-0 fw-bold"
                      style={getNavLinkStyle(isProductsActive)}
                    >
                      Products
                    </NavLink>
                  );
                })()}
              </NavItem>
              {!isDealerUser && !isAdminUser && hasAnyOrder && (
                <>
                  <span
                    className="text-secondary mx-2"
                    aria-hidden="true"
                    style={{ lineHeight: 1 }}
                  >
                    •
                  </span>
                  <NavItem>
                    {(() => {
                      const ordersPath = `${basePath}/orders`;
                      const isOrdersActive = isNavActive(ordersPath, true);
                      return (
                        <NavLink
                          tag={Link}
                          to={ordersPath}
                          className="px-0 fw-bold"
                          style={getNavLinkStyle(isOrdersActive)}
                        >
                          My Orders
                        </NavLink>
                      );
                    })()}
                  </NavItem>
                </>
              )}
            </>
          )}

          {canShowSizeChart && (
            <>
              <NavLink
                href="#"
                className="text-light p-0 small fw-semibold ms-3"
                onClick={(e) => {
                  e.preventDefault();
                  setIsSizingGridOpen(true);
                }}
                title={isBuyerUser ? "Size Chart" : "Sizes"}
              >
                {isBuyerUser ? "Size Chart" : "Sizes"}
              </NavLink>
            </>
          )}
        </div>

        {/* Mobile icons - right aligned */}
        <div className="d-lg-none d-flex align-items-center justify-content-end gap-3">
          {!isDealerUser && (
            <NavLink
              href="tel:+919150469996"
              className="text-light p-0"
              title="Call +91 91504 69996"
              aria-label="Call +91 91504 69996"
              onClick={() => setIsProfileMenuOpen(false)}
            >
              <i className="fa-solid fa-phone fs-5" />
            </NavLink>
          )}
          {isLoggedIn ? (
            <div className="d-flex position-relative">
              {isDealerUser ? (
                <div
                  className="text-secondary small me-2 d-flex align-items-center"
                  style={{
                    border: "1px solid #666",
                    borderRadius: 5,
                    padding: "0 5px",
                  }}
                >
                  Dealer
                </div>
              ) : isAdminUser ? (
                <div
                  className="text-secondary small me-2 d-flex align-items-center"
                  style={{
                    border: "1px solid #666",
                    borderRadius: 5,
                    padding: "0 5px",
                  }}
                >
                  Admin
                </div>
              ) : null}
              <NavLink
                href="#"
                className="p-0 text-light"
                onClick={(e) => {
                  e.preventDefault();
                  setIsProfileMenuOpen((prev) => !prev);
                }}
                title="Profile"
              >
                <i className="fa-solid fa-user fs-5"></i>
              </NavLink>
              {isProfileMenuOpen ? (
                <div
                  className="position-absolute end-0 mt-2 bg-white rounded shadow border"
                  style={{ minWidth: 180, zIndex: 1200, top: "100%" }}
                >
                  <div className="px-3 py-2 border-bottom small text-dark fw-semibold text-truncate">
                    {userLabel}
                  </div>
                  {isBuyerUser ? (
                    <button
                      type="button"
                      className="btn btn-link text-decoration-none text-dark w-100 text-start px-3 py-2 border-bottom small"
                      onClick={() => {
                        setIsProfileMenuOpen(false);
                        onEditProfileClick?.();
                      }}
                    >
                      <i className="fa-solid fa-pen-to-square me-2" />
                      Edit Profile
                    </button>
                  ) : null}
                  {isBuyerUser && hasAnyCreditNote ? (
                    <Link
                      to={`${basePath}/credit-notes`}
                      className="d-block text-decoration-none text-dark px-3 py-2 border-bottom small"
                      onClick={() => setIsProfileMenuOpen(false)}
                    >
                      <i className="fa-solid fa-receipt me-2" />
                      My Credits
                    </Link>
                  ) : null}
                  <button
                    type="button"
                    className="btn btn-link text-decoration-none text-dark w-100 text-start px-3 py-2"
                    onClick={() => {
                      setIsProfileMenuOpen(false);
                      onAuthClick();
                    }}
                  >
                    <i className="fa-solid fa-power-off me-2" />
                    Logout
                  </button>
                </div>
              ) : null}
            </div>
          ) : (
            <NavLink
              href="#"
              className="p-0 text-light"
              onClick={(e) => {
                e.preventDefault();
                setIsProfileMenuOpen(false);
                onAuthClick();
              }}
              title="Login"
            >
              <i className="fa-solid fa-user fs-5"></i>
            </NavLink>
          )}
        </div>

        {/* Search Box - Desktop only */}
        {/* <div className="d-none d-lg-flex align-items-center mx-4" style={{ display: 'none !important' }}>
          <StoreSearchBox
            placeholder="Search products..."
            size="sm"
            showLoading={true}
          />
        </div> */}

        {/* Right-end icons with neat spacing */}
        <div className="d-none d-lg-flex align-items-center ms-auto">
          <div className="me-4">
            <NavLink
              href="tel:+919150469996"
              className="text-light"
              title="Call +91 91504 69996"
              aria-label="Call +91 91504 69996"
              onClick={() => setIsProfileMenuOpen(false)}
            >
              <i className="fa-solid fa-phone fs-5" />
            </NavLink>
          </div>
          {isLoggedIn ? (
            <div className="d-flex align-items-center position-relative">
              {isDealerUser ? (
                <div
                  className="text-secondary small me-2"
                  style={{
                    border: "1px solid #666",
                    borderRadius: 5,
                    padding: "0 5px",
                  }}
                >
                  Dealer
                </div>
              ) : isAdminUser ? (
                <div
                  className="text-secondary small me-2"
                  style={{
                    border: "1px solid #666",
                    borderRadius: 5,
                    padding: "0 5px",
                  }}
                >
                  Admin
                </div>
              ) : null}
              <NavLink
                href="#"
                className="text-light"
                onClick={(e) => {
                  e.preventDefault();
                  setIsProfileMenuOpen((prev) => !prev);
                }}
                title="Profile"
              >
                <i className="fa-solid fa-user fs-5" />
              </NavLink>
              {isProfileMenuOpen ? (
                <div
                  className="position-absolute end-0 mt-2 bg-white rounded shadow border"
                  style={{ minWidth: 200, zIndex: 1200, top: "100%" }}
                >
                  <div className="px-3 py-2 border-bottom small text-dark fw-semibold text-truncate">
                    {userLabel}
                  </div>
                  {isBuyerUser ? (
                    <button
                      type="button"
                      className="btn btn-link text-decoration-none text-dark w-100 text-start px-3 py-2 border-bottom small"
                      onClick={() => {
                        setIsProfileMenuOpen(false);
                        onEditProfileClick?.();
                      }}
                    >
                      <i className="fa-solid fa-pen-to-square me-2" />
                      Edit Profile
                    </button>
                  ) : null}
                  {isBuyerUser && hasAnyCreditNote ? (
                    <Link
                      to={`${basePath}/credit-notes`}
                      className="d-block text-decoration-none text-dark px-3 py-2 border-bottom small"
                      onClick={() => setIsProfileMenuOpen(false)}
                    >
                      <i className="fa-solid fa-receipt me-2" />
                      My Credits
                    </Link>
                  ) : null}
                  <button
                    type="button"
                    className="btn btn-link text-decoration-none text-dark w-100 text-start px-3 py-2"
                    onClick={() => {
                      setIsProfileMenuOpen(false);
                      onAuthClick();
                    }}
                  >
                    <i className="fa-solid fa-power-off me-2" />
                    Logout
                  </button>
                </div>
              ) : null}
            </div>
          ) : (
            <NavLink
              href="#"
              className="text-light"
              onClick={(e) => {
                e.preventDefault();
                // Keep auth flow in modal for header icon as well.
                setIsProfileMenuOpen(false);
                onAuthClick();
              }}
              title="Login"
            >
              <i className="fa-solid fa-user fs-5"></i>
            </NavLink>
          )}
        </div>
      </Container>
      <SizingGrid isOpen={isSizingGridOpen} setIsOpen={setIsSizingGridOpen} />
    </Navbar>
  );
};

export default NavBar;
